import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type { ItemKind, Movement, MovementKind, StockOverview } from './types'

async function authorized(eventId: string | undefined) {
  return { token: await accessToken(), ...(eventId ? { eventId } : {}) }
}

export function useStock(eventId: string | undefined, alertsOnly: boolean) {
  return useQuery({
    queryKey: ['inventory', eventId, alertsOnly],
    queryFn: async () =>
      apiClient.get<StockOverview>(
        `/ops/inventory?alerts_only=${alertsOnly}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId),
    // Plusieurs personnes saisissent en même temps le soir J : une réserve,
    // un bar, une cuisine. Rafraîchir évite de compter deux fois la même
    // livraison parce qu'un écran n'avait pas vu l'autre.
    refetchInterval: 60_000,
  })
}

export function useMovements(eventId: string | undefined, refId: string | null) {
  return useQuery({
    queryKey: ['inventory-movements', eventId, refId],
    queryFn: async () =>
      apiClient.get<Movement[]>(
        refId ? `/ops/inventory/movements?ref_id=${refId}` : '/ops/inventory/movements',
        await authorized(eventId),
      ),
    enabled: Boolean(eventId),
  })
}

/** Invalide l'état ET le journal : les deux changent d'un seul geste. */
function useStockMutation<TPayload>(
  eventId: string | undefined,
  call: (payload: TPayload, auth: Awaited<ReturnType<typeof authorized>>) => Promise<unknown>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: TPayload) => call(payload, await authorized(eventId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory-movements'] })
      // Le catalogue du poste de vente affiche « épuisé » : il vient de changer.
      void queryClient.invalidateQueries({ queryKey: ['pos-catalogue'] })
    },
  })
}

export function useRecordMovement(eventId: string | undefined) {
  return useStockMutation<{
    item_type: ItemKind
    ref_id: string
    kind: Exclude<MovementKind, 'sale'>
    quantity: number
    reason: string | null
  }>(eventId, (payload, auth) => apiClient.post('/ops/inventory/movements', payload, auth))
}

export function useCount(eventId: string | undefined) {
  return useStockMutation<{
    item_type: ItemKind
    ref_id: string
    counted: number
    reason: string | null
  }>(eventId, (payload, auth) => apiClient.post('/ops/inventory/count', payload, auth))
}

/** Le prix d'achat : saisi en réserve, facture en main. */
export function useUnitCost(eventId: string | undefined) {
  return useStockMutation<{
    item_type: ItemKind
    ref_id: string
    unit_cost_minor: number | null
  }>(eventId, (payload, auth) => apiClient.put('/ops/inventory/unit-cost', payload, auth))
}

export function useThreshold(eventId: string | undefined) {
  return useStockMutation<{ item_type: ItemKind; ref_id: string; threshold: number | null }>(
    eventId,
    (payload, auth) => apiClient.put('/ops/inventory/threshold', payload, auth),
  )
}
